import { useState, memo, useRef } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Chip from '@zenbusiness/zui/Chip';
import FormControl from '@zenbusiness/zui/FormControl';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import { US_NEXUS_OPTIONS } from '#client/constants/additionalInfo';
import addToString from '#client/utils/addToString';

const options = addToString(US_NEXUS_OPTIONS);

const UsNexusUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  dataChangeHandler = () => {},
  disabled,
  data
}) => {
  const usNexusArray = data?.usNexus === 'notAvailable' ? [] : data.usNexus?.split(',');
  const getInitialSelection = () => {
    if (data?.usNexus === 'notAvailable') {
      return 'notAvailable';
    }
    if (usNexusArray?.some((val) => options.some((opt) => opt.value === val))) {
      return 'yes';
    }
    return 'yes';
  };

  const usNexusRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState(getInitialSelection);
  const [usNexusList, setUsNexusList] = useState(
    usNexusArray
      ? usNexusArray
          .map((usNexus) => options.find((option) => option.value === usNexus))
          .filter(Boolean)
      : []
  );
  const trackUsNexusChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  const onDataChanged = (event, option) => {
    const formatValue = option || usNexusList.map((usNexus) => usNexus.value).join(',');
    trackUsNexusChangeClick(event);

    if (formatValue) {
      field.setLoading();
    }
    dataChangeHandler(
      !!formatValue,
      { usNexus: formatValue },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave, isError);
        forceUpdate();
      },
      field.key
    );
  };

  const handleChangeRadioOption = (event) => {
    setSelectedOption(event.target.value);

    if (event.target.value === 'notAvailable') {
      onDataChanged(event, event.target.value);
    }
  };

  const handleChangeList = (event, newValue) => {
    setUsNexusList(newValue);
  };

  return (
    <FormControl
      fullWidth
      sx={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
      variant="outlined"
    >
      <RadioGroup
        name="regulated-status-radio-group"
        onChange={handleChangeRadioOption}
        value={selectedOption}
      >
        <FormControlLabel
          control={
            <Radio
              color="info"
              disabled={disabled}
              size="medium"
            />
          }
          label="Yes"
          value="yes"
        />
        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Radio
                color="info"
                disabled={disabled}
                size="medium"
              />
            }
            label="No"
            value="notAvailable"
          />
          {selectedOption === 'notAvailable' && (
            <Text
              color="tertiary"
              size="md"
              sx={{ marginLeft: 5 }}
            >
              ZenBusiness Banking requires operational ties to the United States. Your application
              may be denied.
            </Text>
          )}
        </Stack>
      </RadioGroup>

      {selectedOption === 'yes' && (
        <Stack spacing={1.5}>
          <Text
            color="tertiary"
            size="md"
          >
            Please specify the nature of your U.S. operations (select all that apply).
          </Text>

          <AutoCompleteInput
            disabled={disabled}
            filterSelectedOptions
            id="us-nexus-list"
            multiple
            onChange={handleChangeList}
            openOnFocus
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={params.inputProps}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ ...params.InputProps }}
                multiple
                onBlur={(event) => onDataChanged(event)}
                ref={usNexusRef}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.label}
                  label={option.label}
                  onDelete={() => {
                    // Focus input when delete a value
                    usNexusRef.current.getElementsByTagName('input')[0].focus();
                    setUsNexusList(usNexusList.filter((usNexusOption) => usNexusOption !== option));
                  }}
                  sx={{ margin: '0 2px 0 2px' }}
                />
              ))
            }
            value={usNexusList}
          />
        </Stack>
      )}
    </FormControl>
  );
};

UsNexusUIField.propTypes = {
  data: PropTypes.shape({
    usNexus: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.data === nextProp.data &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(UsNexusUIField, areEqual);
